import React, { useState, useEffect } from "react";
import CropImageModal from "./CropImageModal";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, deleteDoc, collection, getDocs, addDoc, getDoc, setDoc } from "firebase/firestore";
import { storage, db, auth } from "../firebase";
import Select from "react-select/creatable";

function ViewPersonModal({ person, customFields = [], onClose, onSave, onDelete, setPeople }) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedPerson, setEditedPerson] = useState({
    ...person,
    picture: person.picture || "",
    originalPicture: person.originalPicture || person.picture || "",
  });
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [tags, setTags] = useState([]); // Global tags
  const [selectedTags, setSelectedTags] = useState(
    person.tags?.map((tag) => ({ value: tag, label: tag })) || []
  );

  // Fetch global tags
  useEffect(() => {
    const fetchGlobalTags = async () => {
      try {
        const tagsCollection = collection(db, "globalTags");
        const tagSnapshot = await getDocs(tagsCollection);
        const fetchedTags = tagSnapshot.docs.map((doc) => ({
          value: doc.data().name,
          label: doc.data().name,
        }));
        setTags(fetchedTags);
      } catch (error) {
        console.error("Error fetching global tags:", error);
      }
    };

    fetchGlobalTags();
  }, []);

  const handleTagChange = async (selectedOptions) => {
    setSelectedTags(selectedOptions);

    const newTags = selectedOptions.map((option) => option.value);
    const existingTagNames = tags.map((tag) => tag.value);
    const newGlobalTags = newTags.filter((tag) => !existingTagNames.includes(tag));

    for (const tag of newGlobalTags) {
      try {
        const tagRef = collection(db, "globalTags");
        await addDoc(tagRef, { name: tag });
        setTags((prevTags) => [...prevTags, { value: tag, label: tag }]);
      } catch (error) {
        console.error(`Error adding global tag: ${tag}`, error);
      }
    }

    setEditedPerson((prev) => ({
      ...prev,
      tags: newTags,
    }));
  };

  const handleCropComplete = async (croppedBlob) => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error("User not authenticated");
      }

      const rosterId = person.rosterId;
      if (!rosterId) {
        throw new Error("Roster ID is missing");
      }

      const storagePath = `users/${userId}/rosters/${rosterId}/people/${editedPerson.id}/cropped-picture-${Date.now()}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, croppedBlob);
      const croppedURL = await getDownloadURL(storageRef);

      setEditedPerson((prev) => ({
        ...prev,
        picture: croppedURL,
      }));

      console.log("Cropped image successfully uploaded:", croppedURL);
    } catch (error) {
      console.error("Error uploading cropped image to Firebase:", error);
      alert("Failed to upload cropped image. Please try again.");
    } finally {
      setIsCropModalOpen(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        setTempImageFile(reader.result);
        setIsCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    } else {
      alert("Please upload a valid image file.");
    }
  };

  const handleCustomFieldChange = (fieldName, value) => {
    setEditedPerson((prev) => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [fieldName]: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error("User not authenticated");
      }
  
      const rosterId = person.rosterId;
      if (!rosterId) {
        throw new Error("Roster ID is missing");
      }
  
      const personRef = doc(
        db,
        "users",
        userId,
        "rosters",
        rosterId,
        "people",
        editedPerson.id
      );
  
      await setDoc(personRef, {
        ...editedPerson,
        tags: selectedTags.map((tag) => tag.value),
      });
  
      console.log("Person successfully saved:", editedPerson);
      onSave(editedPerson);
      setIsEditMode(false);
    } catch (error) {
      console.error("Error saving person:", error);
      alert("Failed to save changes. Please try again.");
    }
  };
  

  const handleEditExistingImage = () => {
    if (editedPerson.originalPicture) {
      setTempImageFile(editedPerson.originalPicture);
      setIsCropModalOpen(true);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this person?");
    if (confirmDelete) {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) {
          throw new Error("User not authenticated");
        }

        const rosterId = person.rosterId;
        if (!rosterId) {
          throw new Error("Roster ID is missing");
        }

        const personRef = doc(
          db,
          "users",
          userId,
          "rosters",
          rosterId,
          "people",
          person.id
        );

        await deleteDoc(personRef);
        onDelete(person.id);
        onClose();
        console.log("Person successfully deleted:", person.id);
      } catch (error) {
        console.error("Error deleting person:", error);
        alert("Failed to delete person. Please try again.");
      }
    }
  };

  const handleArchive = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        throw new Error("User not authenticated");
      }
  
      const archiveRef = doc(db, "users", userId, "archivedPeople", person.id);
  
      // Check if the person is already archived
      const archiveSnap = await getDoc(archiveRef);
      if (archiveSnap.exists()) {
        console.warn("Person is already archived:", person.id);
        return;
      }
  
      // Add person to archive
      await setDoc(archiveRef, { ...person, rosterId: null });
  
      // Remove person from the roster
      const rosterRef = doc(
        db,
        "users",
        userId,
        "rosters",
        person.rosterId,
        "people",
        person.id
      );
      await deleteDoc(rosterRef);
  
      // Update local state
      if (setPeople) {
        setPeople((prevPeople) => prevPeople.filter((p) => p.id !== person.id));
      } else {
        console.warn("setPeople is not defined.");
      }
  
      console.log("Person successfully archived:", person.id);
  
      // Notify parent and close modal
      onDelete(person.id);
      onClose();
    } catch (error) {
      console.error("Error archiving person:", error);
      alert("Failed to archive person. Please try again.");
    }
  };
  

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "8px",
          width: "450px",
          maxHeight: "95vh",
          overflowY: "auto",
          textAlign: "left",
          position: "relative",
          fontFamily: "'Arial', sans-serif",
          WebkitOverflowScrolling: "touch",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "-10px",
            right: "-7px",
            backgroundColor: "#ffffff00",
            color: "black",
            border: "none",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            fontSize: "25px",
            cursor: "pointer",
            zIndex: 2,
          }}
        >
          x
        </button>

        {/* Image Section */}
        <div style={{ textAlign: "center", marginBottom: "15px", position: "relative" }}>
          {editedPerson.picture ? (
            <div
              style={{
                position: "relative",
                width: "100%",
                height: "400px",
                backgroundColor: "#f0f0f0",
                borderRadius: "8px",
                overflow: "hidden"
              }}
            >
              <img
                src={editedPerson.picture}
                alt="Profile"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
              {isEditMode && (
                <button
                  onClick={handleEditExistingImage}
                  style={{
                    position: "absolute",
                    top: "5px",
                    left: "5px",
                    backgroundColor: "white",
                    border: "2px solid black",
                    borderRadius: "50%",
                    width: "30px",
                    height: "30px",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 3,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="black"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      width: "16px",
                      height: "16px",
                    }}
                  >
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                  </svg>
                </button>
              )}
            </div>
          ) : (
            <div
              style={{
                width: "100%",
                height: "300px",
                backgroundColor: "#f0f0f0",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <p style={{ color: "#888" }}>No image available</p>
            </div>
          )}
        </div>

        {/* Edit/View Mode Logic */}
        {isEditMode ? (
          <div>
            <h3 style={{ textAlign: "center", marginBottom: "15px" }}>Edit Information</h3>
            <label>Name:</label>
            <input
              type="text"
              placeholder="Name"
              value={editedPerson.name}
              onChange={(e) => setEditedPerson({ ...editedPerson, name: e.target.value })}
              style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
            />
            <label>Age:</label>
            <input
              type="number"
              placeholder="Age"
              value={editedPerson.age}
              onChange={(e) => setEditedPerson({ ...editedPerson, age: e.target.value })}
              style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
            />
            <label>Details:</label>
            <textarea
              placeholder={`Add some details about ${editedPerson.name}`}
              value={editedPerson.details}
              onChange={(e) => setEditedPerson({ ...editedPerson, details: e.target.value })}
              style={{ width: "100%", padding: "8px", marginBottom: "10px", minHeight: "60px" }}
            />
            <label>Social Media:</label>
            <input
              type="url"
              placeholder="Social Media Link"
              value={editedPerson.socialMedia}
              onChange={(e) => setEditedPerson({ ...editedPerson, socialMedia: e.target.value })}
              style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
            />
            <label htmlFor="upload-image" style={{ marginBottom: "5px", display: "block" }}>
              Change pic:
            </label>
            <input
              type="file"
              accept="image/*"
              id="upload-image"
              onChange={(e) => handleImageUpload(e)}
              style={{
                fontSize: "0.9rem",
                padding: "10px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                cursor: "pointer",
                width: "100%",
                marginBottom: "15px",
              }}
            />


            {/* Custom Fields */}
            {customFields.map((field, index) => (
              <div key={index} style={{ marginBottom: "10px" }}>
                <label>{field.name}:</label>
                {field.type === "text" && (
                  <input
                    type="text"
                    value={editedPerson.customFields?.[field.name] || ""}
                    onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                    style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
                  />
                )}
                {field.type === "scale" && (
                  <input
                    type="range"
                    min="0"
                    max="5"
                    value={editedPerson.customFields?.[field.name] || 0}
                    onChange={(e) => handleCustomFieldChange(field.name, Number(e.target.value))}
                    style={{ width: "100%" }}
                  />
                )}
              </div>
            ))}

<           label>Tags:</label>
            <Select
              isMulti
              options={tags}
              value={selectedTags}
              onChange={handleTagChange}
              placeholder="Select or create tags"
              styles={{
                container: (provided) => ({ ...provided, marginBottom: "10px" }),
              }}
            />

<div
  style={{
    display: "flex",
    justifyContent: "space-between",
    marginTop: "15px",
    alignItems: "center",
  }}
>
  <div style={{ display: "flex", gap: "10px" }}>
    <button
      onClick={handleDelete}
      style={{
        backgroundColor: "red",
        color: "white",
        padding: "10px",
        borderRadius: "8px",
        cursor: "pointer",
      }}
    >
      Delete
    </button>

    <button
      onClick={handleArchive}
      style={{
        backgroundColor: "#007BFF", // Light blue to indicate secondary action
        color: "white",
        padding: "10px",
        borderRadius: "8px",
        cursor: "pointer",
      }}
    >
      Archive
    </button>
  </div>

  <button
    onClick={handleSave}
    style={{
      backgroundColor: "black",
      color: "white",
      padding: "10px",
      borderRadius: "8px",
      cursor: "pointer",
    }}
  >
    Save Changes
  </button>
</div>

          </div>
        ) : (
          <div>
            <h2 style={{ fontSize: "20px", fontWeight: "bold", marginBottom: "10px" }}>
              {editedPerson.name} {editedPerson.age && ` ${editedPerson.age}`}
            </h2>
            <p>{editedPerson.details || ""}</p>
            {editedPerson.socialMedia && (
              <p>
                <a
                  href={editedPerson.socialMedia}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#007bff" }}
                >
                  {editedPerson.socialMedia}
                </a>
              </p>
            )}
            <p style={{marginBottom: "10px"}} ></p>
                {/* Upcoming Event Section */}
    {person.upcomingEvent ? (
      <p>
        <strong>Upcoming date:</strong> {person.upcomingEvent}
      </p>
    ) : (
      <p>No dates planned</p>
    )}
  {/* Tags Section */}
  {editedPerson.tags?.length > 0 && (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "5px",
        marginTop: "10px",
      }}
    >
      {editedPerson.tags.map((tag) => (
        <span
          key={tag}
          style={{
            fontSize: "0.75rem",
            color: "#555",
            backgroundColor: "#f5f5f5",
            border: "1px solid #ddd",
            borderRadius: "12px",
            padding: "2px 8px",
          }}
        >
          {tag}
        </span>
      ))}
    </div>
  )}

            <button
              onClick={() => setIsEditMode(true)}
              style={{
                backgroundColor: "black",
                color: "white",
                padding: "10px",
                borderRadius: "8px",
                cursor: "pointer",
                marginTop: "15px",
              }}
            >
              Edit
            </button>
          </div>
        )}
      </div>

      {isCropModalOpen && (
        <CropImageModal
          imageFile={tempImageFile}
          onCropComplete={handleCropComplete}
          onClose={() => setIsCropModalOpen(false)}
        />
      )}
    </div>
  );
}

export default ViewPersonModal;
